import React, { type FC } from 'react'
import { t } from 'i18next'
import { ContentContainer } from '@awell/ui-kit/components/ContentContainer'
import { ScrollContainer } from '@awell/ui-kit/components/ScrollContainer'
import {
  SkeletonLoader,
  Table,
  TableBody,
  TableHeader,
  TableHead,
  TableRow,
  TableCell
} from '@awell-health/design-system'

export const WebhookDetailsSkeleton: FC = ({}): JSX.Element => {
  const tableColumns = [
    { title: t('timestamp'), field: 'timestamp' },
    { title: t('status'), field: 'status' },
    { title: t('action_other'), field: 'action' }
  ]

  return (
    <ScrollContainer className="bg-white h-full">
      <ContentContainer>
        <article>
          {/* Header with back button and title */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SkeletonLoader className="h-12 w-12 rounded-lg" />
              <SkeletonLoader className="h-6 w-48" />
            </div>
            <SkeletonLoader className="h-10 w-10 rounded-lg" />
          </div>

          {/* Info fields section */}
          <div className="mx-5 flex flex-col gap-5">
            <div className="flex items-center justify-between mt-5">
              <div className="flex gap-10">
                {/* URL field skeleton */}
                <div className="flex items-start gap-2">
                  <SkeletonLoader className="h-6 w-8 mb-1" />
                  <SkeletonLoader className="h-6 w-64" />
                </div>

                {/* Triggered at field skeleton */}
                <div className="flex items-start gap-2">
                  <SkeletonLoader className="h-6 w-16 mb-1" />
                  <SkeletonLoader className="h-6 w-32" />
                </div>

                {/* Trigger event field skeleton */}
                <div className="flex items-start gap-2">
                  <SkeletonLoader className="h-6 w-16 mb-1" />
                  <SkeletonLoader className="h-6 w-24" />
                </div>

                {/* Status field skeleton */}
                <div className="flex items-start gap-2">
                  <SkeletonLoader className="h-6 w-12 mb-1" />
                  <SkeletonLoader className="h-6 w-20" />
                </div>
              </div>
            </div>

            {/* Logs section */}
            <section>
              <div className="text-lg font-medium mb-4">{t('logs')}</div>
              <Table>
                <TableHeader>
                  <TableRow>
                    {tableColumns.map(({ field, title }) => (
                      <TableHead key={field} sortable={false}>
                        {title}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...Array(3).keys()].map(i => (
                    <TableRow key={i}>
                      <TableCell>
                        <SkeletonLoader className="h-4 w-32" />
                      </TableCell>
                      <TableCell>
                        <SkeletonLoader className="h-6 w-32" />
                      </TableCell>
                      <TableCell>
                        <SkeletonLoader className="h-8 w-32" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </section>

            {/* Request body section */}
            <section>
              <div className="flex flex-col gap-3">
                <div className="text-lg font-medium">{t('request_body')}</div>
                <div className="bg-slate-100 rounded-md p-4">
                  <SkeletonLoader className="h-48 w-full rounded" />
                </div>
              </div>
            </section>
          </div>
        </article>
      </ContentContainer>
    </ScrollContainer>
  )
}

WebhookDetailsSkeleton.displayName = 'WebhookDetailsSkeleton'
